import { Box, Card, CardMedia, Divider, Grid, Link, Stack, SxProps, Theme, Typography } from "@mui/material";
import { Preview } from "@mui/icons-material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { authentication } from "e.sic-platform.webclient/src/services/authentication/Authentication";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import DemoSamplesDialog from "./DemoSamplesDialog";

export interface FeatureCardProps {
    title: string;
    description: string;
    image: string | React.ReactNode;
    route: string;
    imageAlt?: string;
    activities?: ActivityTypes[];
    showSamples?: boolean;
    /** Overrides the "sarida™" wordmark font styling. Defaults to Georgia italic serif. */
    brandFontSx?: SxProps<Theme>;
}

export default observer(function FeatureCard(props: FeatureCardProps) {
    const { title, description, image, route, imageAlt, activities, showSamples = false, brandFontSx } = props;
    const navigate = useNavigate();
    const [samplesOpen, setSamplesOpen] = useState(false);

    const goTo = useCallback(() => {
        if (route.startsWith("https")) {
            const newWindow = window.open(route, "_blank", "noopener,noreferrer");
            if (newWindow) newWindow.opener = null;
        } else if (authentication.anonymous) {
            // Signed-out guest opening an app → trigger login, then return to this route.
            authentication.loginToAccess(route);
        } else {
            navigate(route);
        }
    }, [route, navigate]);

    const openSamples = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setSamplesOpen(true);
    }, []);

    // The shop is public: guests can review prices; login is only asked for at checkout.
    const openPricing = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            navigate("/shop");
        },
        [navigate]
    );

    return (
        <Grid item xs={12} xl={6} sx={{ display: "flex", justifyContent: "center" }}>
            <Card
                elevation={4}
                onClick={goTo}
                role="button"
                tabIndex={0}
                aria-label={translationService.translate(title)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goTo();
                    }
                }}
                sx={{
                    cursor: "pointer",
                    // The 70px taken off the card all comes off the image (370 -> 300) so the
                    // text column stays 252px wide: at that width the tallest card content
                    // (2-line title + 4-line description) already fills ~325px of the 340px
                    // usable height, so a narrower text column would overflow and get clipped.
                    width: 580,
                    maxWidth: "100%",
                    height: 380,
                    display: "flex",
                    flexDirection: "row",
                    borderRadius: 3,
                    overflow: "hidden",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: 14,
                    },
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                    },
                }}
            >
                {typeof image === "string" ? (
                    <CardMedia
                        component="img"
                        image={image}
                        alt={imageAlt ?? translationService.translate(title)}
                        sx={{ width: 300, height: "100%", objectFit: "cover", flexShrink: 0 }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: 300,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                            flexShrink: 0,
                        }}
                    >
                        {image}
                    </Box>
                )}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        pl: 1.5,
                        pr: 2,
                        py: 2.5,
                    }}
                >
                    {/* TOP — title block + description */}
                    <Stack spacing={1.2}>
                        <Box>
                            <Typography
                                sx={[
                                    { fontSize: "2rem", color: "primary.main", lineHeight: 1 },
                                    // Default wordmark look; a card may override the font via brandFontSx.
                                    !brandFontSx && {
                                        fontFamily: "'Georgia', 'Times New Roman', serif",
                                        fontStyle: "italic",
                                    },
                                    ...(Array.isArray(brandFontSx) ? brandFontSx : [brandFontSx]),
                                ]}
                            >
                                sarida
                                <Box component="sup" sx={{ fontSize: "0.5em", ml: 0.2, top: "-0.6em" }}>
                                    &trade;
                                </Box>
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
                                {translationService.translate(title)}
                            </Typography>
                        </Box>
                        <Divider sx={{ borderColor: "primary.main", borderBottomWidth: 2, width: 60 }} />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.5,
                                whiteSpace: "pre-line",
                            }}
                        >
                            {translationService.translate(description)}
                        </Typography>
                    </Stack>

                    {/* FREE AREA — two blank lines of space after the description. */}
                    <Box sx={{ height: 56 }} />

                    {/* CAPTION + LINK — tightly grouped, sit just above the cost */}
                    {showSamples && (
                        <Stack alignItems="flex-end" spacing={0.25}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.4, fontStyle: "italic", textAlign: "right" }}
                            >
                                {translationService.translate("home.featureCard.showSamplesSubtext")}
                            </Typography>
                            <Link
                                component="button"
                                type="button"
                                onClick={openSamples}
                                underline="hover"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                }}
                            >
                                <Preview fontSize="small" />
                                {translationService.translate("home.featureCard.showSamples")}
                            </Link>
                        </Stack>
                    )}

                    {/* PRICING — two blank lines of space above it (after the link). */}
                    {activities !== undefined && (
                        <Stack alignItems="flex-end" spacing={0.25} sx={{ mt: "56px" }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.4, fontStyle: "italic", textAlign: "right" }}
                            >
                                {translationService.translate("home.featureCard.pricingSubtext")}
                            </Typography>
                            <Link
                                component="button"
                                type="button"
                                onClick={openPricing}
                                underline="hover"
                                sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                            >
                                {translationService.translate("home.featureCard.viewPricing")}
                            </Link>
                        </Stack>
                    )}
                </Box>
            </Card>
            {showSamples && <DemoSamplesDialog open={samplesOpen} onClose={() => setSamplesOpen(false)} />}
        </Grid>
    );
});
